/**
 * TalentPoolModal — small reason picker for sending a candidate to the
 * "vivier" (talent pool). Used from CandidateProfile and CallActionModal.
 *
 * Loads the canonical reasons from the backend on first open so the
 * dropdown stays in sync if we add reasons later.
 */
import { motion } from "framer-motion";
import { Archive, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";


export default function TalentPoolModal({ candidate, defaultReason = "cant_reach", onClose, onParked }) {
    const [reasons, setReasons] = useState({});
    const [reason, setReason] = useState(defaultReason);
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let active = true;
        api.get("/candidates/talent-pool/reasons")
            .then((r) => { if (active) setReasons(r.data?.reasons || {}); })
            .catch(() => { /* graceful — keep dropdown empty if it fails */ });
        return () => { active = false; };
    }, []);

    async function submit() {
        if (!reason) {
            toast.error("Sélectionnez une raison");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/candidates/${candidate.id}/talent-pool`, {
                reason,
                note: note.trim() || null,
            });
            toast.success(`${candidate.first_name} ajouté(e) au vivier ✅`, {
                description: reasons[reason] || reason,
                duration: 5000,
            });
            window.dispatchEvent(new CustomEvent("vivier:changed"));
            onParked?.(data);
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Action impossible");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
                data-testid="talent-pool-modal"
            >
                <header className="px-6 py-5 border-b border-shariti-gray flex items-start gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-shariti-orange-soft flex items-center justify-center text-shariti-orange shrink-0">
                        <Archive className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-lg font-black text-shariti-black">
                            Mettre {candidate.first_name} au vivier
                        </h2>
                        <p className="text-xs font-medium text-slate-500 mt-0.5">
                            Le candidat sort de la file active mais reste accessible depuis la page Vivier pour le recontacter plus tard.
                        </p>
                    </div>
                </header>

                <div className="p-6 space-y-4">
                    <label className="block">
                        <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5 block">
                            Raison
                        </span>
                        <select
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="input-shariti w-full"
                            data-testid="talent-pool-reason-select"
                        >
                            {Object.entries(reasons).map(([id, label]) => (
                                <option key={id} value={id}>{label}</option>
                            ))}
                        </select>
                    </label>

                    <label className="block">
                        <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5 block">
                            Note (optionnelle)
                        </span>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={3}
                            placeholder="Ex: Très motivé mais part en stage 3 mois — recontacter en septembre"
                            className="input-shariti w-full resize-none"
                            data-testid="talent-pool-note"
                        />
                    </label>

                    <div className="rounded-2xl bg-shariti-cream p-3 text-[11px] text-slate-600 leading-snug">
                        <strong>Score IA actuel :</strong> {candidate.ai_score || "—"}/100 — vous pourrez le retrouver et le réactiver en un clic depuis la page <em>Vivier</em>.
                    </div>
                </div>

                <footer className="px-6 py-4 bg-shariti-cream/50 border-t border-shariti-gray flex items-center justify-end gap-2">
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        data-testid="talent-pool-cancel"
                        className="px-4 py-2 rounded-full text-xs font-extrabold text-slate-600 hover:bg-white transition"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={submit}
                        disabled={submitting}
                        data-testid="talent-pool-confirm"
                        className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold text-xs uppercase tracking-wider px-5 py-2.5 rounded-full hover:bg-[#d66528] disabled:opacity-50 transition shadow-btn"
                    >
                        {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
                        Mettre au vivier
                    </button>
                </footer>
            </motion.div>
        </div>
    );
}
